import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, Tag, Star, Eye, EyeOff } from 'lucide-react';
import Swal from 'sweetalert2';
import { Banner } from '../../components/common/Banner';
import {
  getAllFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
  getAllCategories,
  createCategory,
  updateCategory,
  renameCategory,
  deleteCategory,
  getRatingStats,
  getRatingsPaginated,
} from '../../services/chatbotAdminService';
import { getChatbotConfig, updateChatbotConfig } from '../../services/chatbotConfigService';

const emptyForm = {
  question: '',
  answer: '',
  category: '',
  keywords: '',
  isActive: true,
  platform: 'all',
};

export default function AdminChatbot() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('faqs');
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);

  const [catForm, setCatForm] = useState({ name: '', responsibleName: '', responsibleEmail: '', responsiblePhone: '' });
  const [catSaving, setCatSaving] = useState(false);
  const [showNewResponsable, setShowNewResponsable] = useState(false);
  const [ratingStats, setRatingStats] = useState(null);
  const [ratings, setRatings] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [faqPage, setFaqPage] = useState(1);
  const [catPage, setCatPage] = useState(1);
  const faqsPerPage = 15;
  const [chatbotEnabled, setChatbotEnabled] = useState(null);
  const [chatbotLoading, setChatbotLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getChatbotConfig();
        setChatbotEnabled(cfg.enabled);
      } catch {
        setChatbotEnabled(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (tab === 'ratings') {
      loadRatingStats();
      loadRatings(1);
    }
  }, [tab]);

  const loadRatingStats = async () => {
    try {
      const data = await getRatingStats();
      setRatingStats(data);
    } catch (error) {
      console.error('Error al cargar estadísticas de rating', error);
    }
  };

  const loadRatings = async (page = 1) => {
    try {
      setRatingsLoading(true);
      const data = await getRatingsPaginated(page, faqsPerPage);
      setRatings(data);
    } catch (error) {
      console.error('Error al cargar valoraciones', error);
      Swal.fire('Error', error.message, 'error');
    } finally {
      setRatingsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [faqsData, catsData] = await Promise.all([
        getAllFaqs(),
        getAllCategories(),
      ]);
      setFaqs(faqsData);
      setCategoriesList(catsData);
      setFaqPage(1);
      setCatPage(1);
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setEditing(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || '',
      keywords: faq.keywords ? faq.keywords.join(', ') : '',
      isActive: faq.isActive,
      platform: faq.platform || 'all',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      Swal.fire('Error', 'La pregunta y la respuesta son obligatorias', 'error');
      return;
    }
    if (!formData.category) {
      Swal.fire('Error', 'Debes seleccionar una categoría', 'error');
      return;
    }

    const payload = {
      question: formData.question.trim(),
      answer: formData.answer.trim(),
      category: formData.category,
      keywords: formData.keywords
        ? formData.keywords.split(',').map((k) => k.trim()).filter(Boolean)
        : undefined,
      isActive: formData.isActive,
      platform: formData.platform,
    };

    const confirm = await Swal.fire({
      title: editing ? '¿Guardar cambios?' : '¿Crear pregunta?',
      text: editing
        ? `¿Estás seguro de actualizar "${formData.question.substring(0, 60)}..."?`
        : `¿Estás seguro de crear la pregunta "${formData.question.substring(0, 60)}..."?`,
      icon: 'question',
      iconColor: '#eab308',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#09274A',
      cancelButtonColor: '#6b7280',
      confirmButtonText: editing ? 'Sí, actualizar' : 'Sí, crear',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      if (editing) {
        await updateFaq(editing.id, payload);
      } else {
        await createFaq(payload);
      }
      await Swal.fire({
        icon: 'success',
        title: editing ? 'Pregunta actualizada' : 'Pregunta creada',
        timer: 1500,
        showConfirmButton: false,
      });
      setShowModal(false);
      await loadData();
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faq) => {
    const result = await Swal.fire({
      title: '¿Eliminar pregunta?',
      text: `¿Estás seguro de eliminar "${faq.question.substring(0, 60)}..."?`,
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteFaq(faq.id);
        await Swal.fire({
          icon: 'success',
          title: 'Pregunta eliminada',
          timer: 1500,
          showConfirmButton: false,
        });
        await loadData();
      } catch (error) {
        Swal.fire('Error', error.message, 'error');
      }
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await updateFaq(faq.id, { isActive: !faq.isActive });
      await loadData();
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  const openCreateCategory = () => {
    setEditingCat(null);
    setCatForm({ name: '', responsibleName: '', responsibleEmail: '', responsiblePhone: '' });
    setShowNewResponsable(false);
    setShowCatModal(true);
  };

  const openEditCategory = (cat) => {
    setEditingCat(cat);
    setCatForm({
      name: cat.name,
      responsibleName: cat.responsibleName || '',
      responsibleEmail: cat.responsibleEmail || '',
      responsiblePhone: cat.responsiblePhone || '',
    });
    setShowNewResponsable(!!cat.responsibleName);
    setShowCatModal(true);
  };

  const handleSaveCategory = async () => {
    const name = catForm.name.trim();
    if (!name || name.length < 2) {
      Swal.fire('Error', 'El nombre debe tener al menos 2 caracteres', 'error');
      return;
    }

    const confirm = await Swal.fire({
      title: editingCat ? '¿Guardar cambios?' : '¿Crear categoría?',
      text: editingCat
        ? `¿Estás seguro de actualizar la categoría "${editingCat.name}"?`
        : `¿Estás seguro de crear la categoría "${name}"?`,
      icon: 'question',
      iconColor: '#eab308',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#09274A',
      cancelButtonColor: '#6b7280',
      confirmButtonText: editingCat ? 'Sí, actualizar' : 'Sí, crear',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    try {
      setCatSaving(true);
      if (editingCat) {
        await updateCategory(editingCat.name, catForm);
        await Swal.fire({
          icon: 'success',
          title: 'Categoría actualizada',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await createCategory(catForm);
        await Swal.fire({
          icon: 'success',
          title: 'Categoría creada',
          timer: 1500,
          showConfirmButton: false,
        });
      }
      setShowCatModal(false);
      await loadData();
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    const result = await Swal.fire({
      title: '¿Eliminar categoría?',
      text: `¿Estás seguro de eliminar "${cat.name}"? Las preguntas asociadas quedarán sin categoría.`,
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteCategory(cat.name);
        await Swal.fire({
          icon: 'success',
          title: 'Categoría eliminada',
          timer: 1500,
          showConfirmButton: false,
        });
        await loadData();
      } catch (error) {
        Swal.fire('Error', error.message, 'error');
      }
    }
  };

  const handleChatbotToggle = async () => {
    try {
      setChatbotLoading(true);
      const newValue = !chatbotEnabled;
      await updateChatbotConfig(newValue);
      setChatbotEnabled(newValue);
      window.dispatchEvent(new CustomEvent('chatbot-config-updated'));
      await Swal.fire({
        icon: 'success',
        title: newValue ? 'Chatbot habilitado para socios' : 'Chatbot oculto para socios',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setChatbotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Banner
        title="Preguntas Frecuentes"
        subtitle="Gestiona las preguntas frecuentes del asistente virtual RDS"
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex bg-white rounded-lg shadow-sm border overflow-hidden">
              <button
                onClick={() => setTab('faqs')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tab === 'faqs'
                    ? 'bg-[#09274A] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Preguntas ({faqs.length})
              </button>
              <button
                onClick={() => setTab('categories')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tab === 'categories'
                    ? 'bg-[#09274A] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Categorías ({categoriesList.length})
              </button>

              <button
                onClick={() => setTab('ratings')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tab === 'ratings'
                    ? 'bg-[#09274A] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Valoraciones
              </button>
            </div>
            {tab === 'faqs' && (
              <button
                onClick={openCreate}
                className="flex items-center gap-2 px-4 py-2 bg-[#09274A] text-white rounded-lg hover:bg-[#0488AF] transition-colors"
              >
                <Plus size={18} />
                Pregunta frecuente
              </button>
            )}
            {tab === 'categories' && (
              <button
                onClick={openCreateCategory}
                className="flex items-center gap-2 px-4 py-2 bg-[#09274A] text-white rounded-lg hover:bg-[#0488AF] transition-colors"
              >
                <Plus size={18} />
                Nueva Categoría
              </button>
            )}
          </div>

          {/* Toggle visibilidad chatbot para socios */}
          {chatbotEnabled !== null && (
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-700">Chatbot socios</div>
                <div className="text-xs text-gray-500">{chatbotEnabled ? 'Visible' : 'Oculto'}</div>
              </div>
              <button
                onClick={handleChatbotToggle}
                disabled={chatbotLoading}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  chatbotEnabled ? 'bg-[#0488AF]' : 'bg-gray-300'
                } ${chatbotLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    chatbotEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#09274A]"></div>
          </div>
        )}

        {!loading && tab === 'faqs' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-sm font-medium text-gray-600">
                  <th className="px-4 py-3">Pregunta</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3 w-28 text-center">Plataforma</th>
                  <th className="px-4 py-3 w-20 text-center">Activo</th>
                  <th className="px-4 py-3 w-32 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(faqsPerPage > 0
                  ? faqs.slice((faqPage - 1) * faqsPerPage, faqPage * faqsPerPage)
                  : faqs
                ).map((faq) => (
                  <tr key={faq.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800 max-w-md truncate">
                      {faq.question}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {faq.category || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        faq.platform === 'web' 
                          ? 'bg-blue-100 text-blue-800' 
                          : faq.platform === 'mobile' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {faq.platform === 'web' ? 'Web' : faq.platform === 'mobile' ? 'Móvil' : 'Todas'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleActive(faq)}
                        className={`inline-flex items-center gap-1 text-sm ${
                          faq.isActive ? 'text-green-600' : 'text-red-500'
                        }`}
                      >
                        {faq.isActive ? <CheckCircle size={18} /> : <XCircle size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(faq)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(faq)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {faqs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      No hay preguntas creadas. Haz clic en "+ Pregunta frecuente" para agregar una.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {faqs.length > faqsPerPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-gray-500">
                  Mostrando {(faqPage - 1) * faqsPerPage + 1}-{Math.min(faqPage * faqsPerPage, faqs.length)} de {faqs.length}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setFaqPage((p) => Math.max(1, p - 1))}
                    disabled={faqPage === 1}
                    className="px-3 py-1 rounded text-sm border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: Math.ceil(faqs.length / faqsPerPage) }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setFaqPage(page)}
                        className={`px-3 py-1 rounded text-sm border ${
                          page === faqPage
                            ? 'bg-[#09274A] text-white border-[#09274A]'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setFaqPage((p) => Math.min(Math.ceil(faqs.length / faqsPerPage), p + 1))
                    }
                    disabled={faqPage === Math.ceil(faqs.length / faqsPerPage)}
                    className="px-3 py-1 rounded text-sm border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'categories' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left text-sm font-medium text-gray-600">
                    <th className="px-4 py-3">Nombre</th>
                    <th className="px-4 py-3">Responsable</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3 w-28 text-center">Preguntas</th>
                    <th className="px-4 py-3 w-32 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {categoriesList.slice((catPage - 1) * faqsPerPage, catPage * faqsPerPage).map((cat) => (
                    <tr key={cat.name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800">
                        <div className="flex items-center gap-2">
                          <Tag size={16} className="text-[#09274A]" />
                          {cat.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {cat.responsibleName || <span className="text-gray-400 italic">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {cat.responsibleEmail ? (
                          <span className="text-gray-600">{cat.responsibleEmail}</span>
                        ) : (
                          <span className="text-gray-400 italic">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-center">{cat.count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditCategory(cat)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(cat)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                    {categoriesList.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                          No hay categorías. Haz clic en "Nueva Categoría" para agregar una.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
            {categoriesList.length > faqsPerPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-gray-500">
                  Mostrando {(catPage - 1) * faqsPerPage + 1}-{Math.min(catPage * faqsPerPage, categoriesList.length)} de {categoriesList.length}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setCatPage((p) => Math.max(1, p - 1))}
                    disabled={catPage === 1}
                    className="px-3 py-1 rounded text-sm border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: Math.ceil(categoriesList.length / faqsPerPage) }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCatPage(page)}
                        className={`px-3 py-1 rounded text-sm border ${
                          page === catPage
                            ? 'bg-[#09274A] text-white border-[#09274A]'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCatPage((p) => Math.min(Math.ceil(categoriesList.length / faqsPerPage), p + 1))
                    }
                    disabled={catPage === Math.ceil(categoriesList.length / faqsPerPage)}
                    className="px-3 py-1 rounded text-sm border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'ratings' && (
          <div className="space-y-6">
            {ratingStats ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl font-bold text-[#09274A]">{ratingStats.totalVotes}</div>
                    <div className="text-sm text-gray-500 mt-1">Votos totales</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl font-bold text-yellow-500">
                      {ratingStats.distribution.length > 0
                        ? (
                            ratingStats.distribution.reduce((sum, d) => sum + d.score * d.count, 0) /
                            ratingStats.totalVotes
                          ).toFixed(1)
                        : '—'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">Promedio general</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl font-bold text-[#0488AF]">{ratingStats.topFaqs.length}</div>
                    <div className="text-sm text-gray-500 mt-1">Preguntas valoradas</div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-[#09274A] mb-4 flex items-center gap-2">
                    <Star size={20} />
                    Distribución de valoraciones
                  </h3>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const item = ratingStats.distribution.find((d) => Number(d.score) === star);
                      const count = item ? Number(item.count) : 0;
                      const pct = ratingStats.totalVotes > 0 ? (count / ratingStats.totalVotes) * 100 : 0;
                      return (
                        <div key={star} className="flex items-center gap-3">
                          <span className="text-sm font-medium w-8 text-gray-600">{star} ★</span>
                          <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-400 rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 w-12 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Star size={16} />
                    Todas las valoraciones
                  </div>
                  {ratingsLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#09274A]"></div>
                    </div>
                  ) : (
                    <>
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 text-left text-sm font-medium text-gray-600">
                            <th className="px-4 py-3">Empresa</th>
                            <th className="px-4 py-3">Pregunta</th>
                            <th className="px-4 py-3 w-24 text-center">Valoración</th>
                            <th className="px-4 py-3 w-32 text-center">Fecha</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {ratings.data.map((r) => (
                            <tr key={r.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-800">{r.companyName}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate">{r.question}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-sm font-medium text-yellow-600">{r.score}/5</span>
                              </td>
                              <td className="px-4 py-3 text-center text-sm text-gray-500">
                                {new Date(r.createdAt).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </td>
                            </tr>
                          ))}
                          {ratings.data.length === 0 && (
                            <tr>
                              <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                                No hay valoraciones registradas aún.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      {ratings.total > faqsPerPage && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                          <span className="text-sm text-gray-500">
                            Mostrando {(ratings.page - 1) * faqsPerPage + 1}-{Math.min(ratings.page * faqsPerPage, ratings.total)} de {ratings.total}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => loadRatings(ratings.page - 1)}
                              disabled={ratings.page === 1}
                              className="px-3 py-1 rounded text-sm border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Anterior
                            </button>
                            {Array.from({ length: Math.ceil(ratings.total / faqsPerPage) }, (_, i) => i + 1).map(
                              (page) => (
                                <button
                                  key={page}
                                  onClick={() => loadRatings(page)}
                                  className={`px-3 py-1 rounded text-sm border ${
                                    page === ratings.page
                                      ? 'bg-[#09274A] text-white border-[#09274A]'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              ),
                            )}
                            <button
                              onClick={() => loadRatings(ratings.page + 1)}
                              disabled={ratings.page === Math.ceil(ratings.total / faqsPerPage)}
                              className="px-3 py-1 rounded text-sm border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Siguiente
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#09274A]"></div>
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#09274A]">
                {editing ? 'Editar Pregunta frecuente' : 'Nueva Pregunta frecuente'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pregunta <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                  placeholder="Ej: ¿Cómo ingresar a la plataforma?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Respuesta <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A] resize-y"
                  placeholder="Escribe la respuesta detallada..."
                />
              </div>

              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría <span className="text-red-500">*</span>
                  </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A] bg-white"
                >
                  <option value="" disabled>Selecciona una categoría</option>
                  {categoriesList.map((cat) => (
                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-[#09274A] focus:ring-[#09274A]"
                />
                <span className="text-sm text-gray-700">Pregunta activa</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plataforma
                </label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A] bg-white"
                >
                  <option value="all">Todas las plataformas</option>
                  <option value="web">Solo Web</option>
                  <option value="mobile">Solo Móvil</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona en qué plataforma será visible esta pregunta
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-[#09274A] hover:bg-[#0488AF] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  editing ? 'Actualizar' : 'Crear pregunta'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#09274A]">
                {editingCat ? 'Editar categoría' : 'Nueva categoría'}
              </h2>
              <button
                type="button"
                onClick={() => setShowCatModal(false)}
                className="p-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={catForm.name}
                  onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                  placeholder="Ej: Acceso a RDS"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable <span className="text-red-500">*</span>
                </label>

                {!showNewResponsable ? (
                  <div className="space-y-2">
                    <select
                      value={catForm.responsibleEmail || ''}
                      onChange={(e) => {
                        const selected = e.target.value;
                        if (!selected) {
                          setCatForm({ ...catForm, responsibleName: '', responsibleEmail: '', responsiblePhone: '' });
                          return;
                        }
                        const cat = categoriesList.find(c => c.responsibleEmail === selected);
                        setCatForm({
                          ...catForm,
                          responsibleName: cat?.responsibleName || '',
                          responsibleEmail: cat?.responsibleEmail || '',
                          responsiblePhone: cat?.responsiblePhone || '',
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A] bg-white"
                    >
                      <option value="">Selecciona un responsable...</option>
                      {[...new Map(
                        categoriesList
                          .filter(c => c.responsibleEmail)
                          .map(c => [c.responsibleEmail, c])
                      ).values()].map((cat) => (
                        <option key={cat.responsibleEmail} value={cat.responsibleEmail}>
                          {cat.responsibleName} - {cat.responsibleEmail}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewResponsable(true)}
                      className="flex items-center gap-1 text-sm text-[#0488AF] hover:text-[#09274A] font-medium"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                      Crear responsable
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre</label>
                        <input
                          type="text"
                          value={catForm.responsibleName}
                          onChange={(e) => setCatForm({ ...catForm, responsibleName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                          placeholder="Ej: Juan Pérez"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          value={catForm.responsibleEmail}
                          onChange={(e) => setCatForm({ ...catForm, responsibleEmail: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                          placeholder="Ej: juan@cidere.cl"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
                      <input
                        type="text"
                        value={catForm.responsiblePhone}
                        onChange={(e) => setCatForm({ ...catForm, responsiblePhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                        placeholder="Ej: +56 9 1234 5678"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNewResponsable(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      ← Seleccionar responsable existente
                    </button>
                  </div>
                )}
              </div>


            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCatModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                disabled={catSaving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={catSaving}
                className="flex-1 px-4 py-2 bg-[#09274A] hover:bg-[#0488AF] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {catSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  editingCat ? 'Guardar cambios' : 'Crear categoría'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
